import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftChoiceUnitListDataAtom,
  giftChoiceUnitListStatusAtom,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import {
  deleteGiftChoiceUnit,
  updateGiftChoiceUnit,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.controller';
import { useGiftChoiceUnitListHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Menu } from '@headlessui/react';
import { DotsHorizontalIcon, PlusIcon, SearchIcon } from '@heroicons/react/outline';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useAsyncFn } from 'react-use';

import styles from '../components/GiftBrandManagement.module.css';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';
import {
  GiftItemInfoUsageLite,
  buildUnitUsageMap,
  usageChipPrice,
  usageChipTitle,
} from '../utils/choiceUnitUsage.utils';

//* 기본으로 표시할 연결 상품 수. 나머지는 펼쳐서 편집 링크를 제공한다.
const USAGE_CHIP_LIMIT = 3;

/**
 * 유닛별 "어떤 상품에 걸려 있는지" 조회 — 상품 목록을 필요한 필드만 한 번 읽어 맵으로 뒤집는다.
 * 실패(라우트 미개방 404 등)하면 null — 이 정보만 숨기고 목록은 그대로 동작한다.
 */
const fetchUnitUsageMap = async (
  role: LowerRole,
): Promise<Map<string, GiftItemInfoUsageLite[]> | null> => {
  try {
    const { data } = await api_.get<GiftItemInfoUsageLite[]>(
      `/${role}/gift-item-info?select=${encodeURIComponent(
        'name isEnabled choiceUnits priceRetail priceRegular priceWhole',
      )}`,
    );
    return buildUnitUsageMap(data);
  } catch {
    return null;
  }
};

/**
 * 정가 대비 도매가 비율 — 원가율(도매가 / 정가 × 100, 단가표 PriceTableView와 동일 정의)과
 * 정가 대비 차이(원가율 − 100, 도매가 쪽이 싸면 음수)를 함께 돌려준다.
 * 정가가 0 이하이거나 도매가가 없으면 계산 불가(null).
 */
const calcCostRate = (
  priceWhole?: number,
  priceRegular?: number,
): { costRate: number; gapRate: number } | null => {
  if (!priceWhole || !priceRegular || priceRegular <= 0) return null;
  const costRate = (priceWhole / priceRegular) * 100;
  return { costRate, gapRate: costRate - 100 };
};

export const GiftChoiceUnitListPage: FC = function GiftChoiceUnitListPage() {
  const { sellerRole } = useAuth();
  const queryClient = useQueryClient();
  const [expandedUsage, setExpandedUsage] = useState<Record<string, boolean>>({});

  useGiftChoiceUnitListHook(sellerRole());
  useGiftItemBrandListHook(sellerRole());

  const status = useRecoilValue(giftChoiceUnitListStatusAtom);
  const data = useRecoilValue(giftChoiceUnitListDataAtom);
  const setData = useSetRecoilState(giftChoiceUnitListDataAtom);

  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  //* 유닛별 "판매중인 상품" 표기용 (실패해도 목록은 정상)
  //* deps는 반드시 role '값' — sellerRole 함수는 매 렌더 새 참조라 넣으면 무한 조회가 된다.
  const role = sellerRole();
  const [{ value: unitUsageMap, loading: loadingUsage }, loadUnitUsage] = useAsyncFn(
    () => fetchUnitUsageMap(role),
    [role],
  );
  useEffect(() => {
    loadUnitUsage();
  }, [loadUnitUsage]);

  //* 필터는 sessionStorage에 유지 — 수정/등록 페이지를 다녀오거나 새로고침해도 그대로 남는다.
  const [searchQuery, setSearchQuery] = useSessionPersistedState(
    'giftChoiceUnitList.q',
    '',
  );
  const [brandFilter, setBrandFilter] = useSessionPersistedState<string>(
    'giftChoiceUnitList.brand',
    'all',
  );
  const [priceMin, setPriceMin] = useSessionPersistedState(
    'giftChoiceUnitList.priceMin',
    '',
  );
  const [priceMax, setPriceMax] = useSessionPersistedState(
    'giftChoiceUnitList.priceMax',
    '',
  );
  const [enabledFilter, setEnabledFilter] = useSessionPersistedState<string>(
    'giftChoiceUnitList.enabled',
    'all',
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  //* 필터가 하나라도 걸려 있는지 (초기화 버튼·결과 수 표기용)
  const hasActiveFilter =
    !!searchQuery ||
    brandFilter !== 'all' ||
    !!priceMin ||
    !!priceMax ||
    enabledFilter !== 'all';

  //* 필터 초기화 — 세션 저장분도 기본값으로 덮어써 다음 방문에 되살아나지 않는다.
  const resetAllFilters = useCallback(() => {
    setSearchQuery('');
    setBrandFilter('all');
    setPriceMin('');
    setPriceMax('');
    setEnabledFilter('all');
  }, [setSearchQuery, setBrandFilter, setPriceMin, setPriceMax, setEnabledFilter]);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    // 검색 필터
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query),
      );
    }

    // 브랜드 필터
    if (brandFilter !== 'all') {
      if (brandFilter === 'none') {
        result = result.filter((item) => !item.giftItemBrand);
      } else {
        result = result.filter((item) => {
          const brandId =
            typeof item.giftItemBrand === 'object'
              ? item.giftItemBrand?._id
              : item.giftItemBrand;
          return brandId === brandFilter;
        });
      }
    }

    // 가격 필터
    const min = priceMin ? Number(priceMin) : null;
    const max = priceMax ? Number(priceMax) : null;
    if (min !== null || max !== null) {
      result = result.filter((item) => {
        const price = item.priceRegular ?? 0;
        if (min !== null && price < min) return false;
        if (max !== null && price > max) return false;
        return true;
      });
    }

    // 판매상태 필터
    if (enabledFilter !== 'all') {
      const isEnabled = enabledFilter === 'enabled';
      result = result.filter((item) => (item.enabled !== false) === isEnabled);
    }

    return result;
  }, [data, searchQuery, brandFilter, priceMin, priceMax, enabledFilter]);

  // 판매상태 토글 핸들러
  const handleToggleEnabled = useCallback(
    async (unit: GiftChoiceUnitData) => {
      const nextEnabled = unit.enabled === false;
      try {
        setTogglingId(unit._id);
        const updated = await updateGiftChoiceUnit(
          unit._id,
          { enabled: nextEnabled },
          sellerRole(),
        );
        setData((prev) => prev.map((item) => (item._id === unit._id ? updated : item)));
      } catch (error) {
        console.error(error);
        alert('상태 변경에 실패했습니다.');
      } finally {
        setTogglingId(null);
      }
    },
    [sellerRole, setData],
  );

  // 삭제 핸들러
  const handleDelete = useCallback(
    async (unit: GiftChoiceUnitData) => {
      if (!confirm(`"${unit.name}" 선택유닛을 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(unit._id);
        await deleteGiftChoiceUnit(unit._id, sellerRole());
        setData((prev) => prev.filter((item) => item._id !== unit._id));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  // 브랜드별 그룹 + 가격 오름차순 정렬
  const groupedData = useMemo(() => {
    const groups: { brandId: string; brandName: string; items: GiftChoiceUnitData[] }[] =
      [];
    const brandMap: Record<string, GiftChoiceUnitData[]> = {};
    const noBrandItems: GiftChoiceUnitData[] = [];

    for (const item of filteredData) {
      if (item.giftItemBrand && typeof item.giftItemBrand === 'object') {
        const id = item.giftItemBrand._id;
        if (!brandMap[id]) brandMap[id] = [];
        brandMap[id]?.push(item);
      } else {
        noBrandItems.push(item);
      }
    }

    const sortByPrice = (a: GiftChoiceUnitData, b: GiftChoiceUnitData) =>
      (a.priceRegular ?? 0) - (b.priceRegular ?? 0);

    for (const brandId of Object.keys(brandMap)) {
      const items = brandMap[brandId] ?? [];
      if (items.length === 0) continue;
      const firstItem = items[0];
      if (!firstItem) continue;
      const brand = firstItem.giftItemBrand;
      const brandName = typeof brand === 'object' ? brand.name : brandId;

      groups.push({ brandId, brandName, items: items.sort(sortByPrice) });
    }

    groups.sort((a, b) => a.brandName?.localeCompare(b.brandName));

    if (noBrandItems.length > 0) {
      groups.push({
        brandId: 'none',
        brandName: '브랜드 없음',
        items: noBrandItems.sort(sortByPrice),
      });
    }

    return groups;
  }, [filteredData]);

  const primaryButton =
    'inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700';
  const secondaryButton =
    'rounded border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50';
  const cell = 'block px-5 py-2 xl:table-cell xl:py-5 xl:align-top';
  const newUnitUrl = `/gift-seller/choice-unit/new${
    brandFilter !== 'all' && brandFilter !== 'none' ? `?brandId=${brandFilter}` : ''
  }`;
  const counts = {
    all: data.length,
    enabled: data.filter((item) => item.enabled !== false).length,
    disabled: data.filter((item) => item.enabled === false).length,
  };

  const renderItem = (item: GiftChoiceUnitData) => {
    const infos = unitUsageMap?.get(item._id) ?? [];
    const shown = expandedUsage[item._id] ? infos : infos.slice(0, USAGE_CHIP_LIMIT);
    const rate = calcCostRate(item.priceWhole, item.priceRegular);
    const editUrl = `/gift-seller/choice-unit/${item._id}/edit`;
    return (
      <tr
        key={item._id}
        className="block border-t border-gray-200 bg-white py-4 hover:bg-gray-50/60 xl:table-row xl:py-0"
      >
        <th scope="row" className={`${cell} xl:w-1/4`}>
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-50 text-lg text-gray-400">
              {item.image?.url ? (
                <img
                  src={fileBucketUrl(item.image.url, 'raw')}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <span aria-hidden>{item.name.charAt(0)}</span>
              )}
            </div>
            <div className="min-w-0">
              <Link
                to={editUrl}
                className="break-words text-base font-semibold text-gray-900 hover:text-indigo-700 hover:underline"
              >
                {item.name}
              </Link>
              {item.description && (
                <p className="mt-1 break-words text-xs font-normal leading-5 text-gray-500">
                  {item.description}
                </p>
              )}
            </div>
          </div>
        </th>
        <td className={cell}>
          <dl className="space-y-1 text-sm">
            <div className="flex gap-3">
              <dt className="text-xs leading-5 text-gray-500">정가</dt>
              <dd className="font-medium tabular-nums">
                {item.priceRegular != null
                  ? `${item.priceRegular.toLocaleString()}원`
                  : '미설정'}
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="text-xs leading-5 text-gray-500">도매</dt>
              <dd className="tabular-nums text-gray-600">
                {item.priceWhole != null
                  ? `${item.priceWhole.toLocaleString()}원`
                  : '미설정'}
              </dd>
            </div>
            {rate && (
              <div className="pt-1 text-xs text-gray-500">
                원가율{' '}
                <span className="tabular-nums text-gray-700">
                  {rate.costRate.toFixed(1)}%
                </span>
                <span
                  className={`ml-2 tabular-nums ${
                    rate.gapRate <= 0 ? 'text-blue-600' : 'text-rose-600'
                  }`}
                >
                  ({rate.gapRate > 0 ? '+' : ''}
                  {rate.gapRate.toFixed(1)}%)
                </span>
              </div>
            )}
          </dl>
        </td>
        <td className={`${cell} xl:w-1/3`}>
          <span className="mb-2 block text-xs text-gray-500 xl:hidden">연결 원상품</span>
          {unitUsageMap ? (
            infos.length ? (
              <div>
                <p className="mb-2 text-xs text-gray-500">
                  전체 {infos.length}개 · 판매중{' '}
                  {infos.filter((info) => info.isEnabled !== false).length}개
                </p>
                <div
                  id={`unit-usage-${item._id}`}
                  className="flex flex-col items-start gap-1.5"
                >
                  {shown.map((info) => {
                    const price = usageChipPrice(info);
                    return (
                      <Link
                        key={info._id}
                        to={`/gift-seller/item-info/${info._id}/edit`}
                        title={usageChipTitle(info)}
                        aria-label={info.name}
                        className="flex max-w-full flex-wrap items-baseline gap-x-2 rounded text-sm text-gray-700 hover:text-indigo-700 hover:underline"
                      >
                        <span className="break-words">{info.name}</span>
                        {price != null && (
                          <span className="shrink-0 text-xs tabular-nums text-gray-500">
                            {price.toLocaleString()}원
                          </span>
                        )}
                        {info.isEnabled === false && (
                          <span className="text-xs text-gray-500">판매종료</span>
                        )}
                        <span aria-hidden className="text-xs text-gray-400">
                          ↗
                        </span>
                      </Link>
                    );
                  })}
                </div>
                {infos.length > USAGE_CHIP_LIMIT && (
                  <button
                    type="button"
                    aria-label={`${item.name} 연결 상품 ${
                      expandedUsage[item._id] ? '접기' : '모두 보기'
                    }`}
                    aria-expanded={Boolean(expandedUsage[item._id])}
                    aria-controls={`unit-usage-${item._id}`}
                    onClick={() =>
                      setExpandedUsage((prev) => ({
                        ...prev,
                        [item._id]: !prev[item._id],
                      }))
                    }
                    className="mt-2 rounded py-1 text-xs font-medium text-gray-600 hover:text-indigo-700"
                  >
                    {expandedUsage[item._id]
                      ? '접기'
                      : `+${infos.length - shown.length}개 더 보기`}
                  </button>
                )}
              </div>
            ) : (
              <span className="text-sm text-gray-500">연결된 상품 없음</span>
            )
          ) : unitUsageMap === null ? (
            <div>
              <p className="text-xs text-gray-500">연결 상품을 불러오지 못했습니다.</p>
              <button
                type="button"
                onClick={loadUnitUsage}
                disabled={loadingUsage}
                className="mt-1 rounded py-1 text-xs text-indigo-700 disabled:opacity-40"
              >
                {loadingUsage ? '조회 중…' : '연결 상품 다시 시도'}
              </button>
            </div>
          ) : (
            <span className="text-xs text-gray-500">연결 상품 조회 중…</span>
          )}
        </td>
        <td className={cell}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-label={`${item.name} 판매 상태`}
              aria-checked={item.enabled !== false}
              onClick={() => handleToggleEnabled(item)}
              disabled={togglingId !== null || deletingId === item._id}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:opacity-40 ${
                item.enabled !== false ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                  item.enabled !== false ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className="whitespace-nowrap text-xs text-gray-600">
              {togglingId === item._id
                ? '변경 중…'
                : item.enabled !== false
                ? '판매중'
                : '판매중단'}
            </span>
          </div>
        </td>
        <td className={cell}>
          <div className="flex items-center gap-1 xl:justify-end">
            <Link
              to={editUrl}
              className="whitespace-nowrap rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              수정
            </Link>
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label={`${item.name} 더보기`}
                disabled={deletingId !== null || togglingId === item._id}
                className="rounded p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
              >
                <DotsHorizontalIcon aria-hidden className="h-5 w-5" />
              </Menu.Button>
              <Menu.Items className="absolute left-0 z-20 mt-1 w-36 rounded-md border border-gray-200 bg-white p-1 shadow-lg focus:outline-none xl:left-auto xl:right-0">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      disabled={deletingId !== null}
                      className={`w-full rounded px-3 py-2 text-left text-sm text-red-700 ${
                        active ? 'bg-red-50' : ''
                      }`}
                    >
                      선택유닛 삭제
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Menu>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-[1440px]">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">선택유닛 관리</h1>
            <p className="mt-2 text-sm text-gray-500">
              쿠폰 상품의 선택유닛과 가격, 연결 상품을 관리합니다.
            </p>
          </div>
          <Link to={newUnitUrl} className={primaryButton}>
            <PlusIcon aria-hidden className="h-4 w-4" />
            선택유닛 등록
          </Link>
        </header>
        <section
          className="rounded-md border border-gray-200 bg-white"
          aria-label="선택유닛 목록"
        >
          <div className="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div
              className="flex flex-wrap items-center gap-2"
              aria-label="판매 상태 필터"
            >
              {(
                [
                  ['all', '전체'],
                  ['enabled', '판매중'],
                  ['disabled', '판매중단'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={enabledFilter === value}
                  onClick={() => setEnabledFilter(value)}
                  className={`rounded px-3 py-2 text-sm ${
                    enabledFilter === value
                      ? 'bg-gray-900 font-medium text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {label} {status === 'success' ? counts[value] : '—'}
                </button>
              ))}
            </div>
            <div className="relative w-full lg:max-w-sm">
              <SearchIcon
                aria-hidden
                className="absolute left-3 top-3 h-4 w-4 text-gray-400"
              />
              <input
                type="search"
                aria-label="선택유닛 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="유닛명, 설명 검색"
                className="w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3 border-t border-gray-100 px-5 py-4">
            <div className="w-full sm:w-auto">
              <label
                htmlFor="unit-brand-filter"
                className="mb-1 block text-xs text-gray-500"
              >
                브랜드 필터
              </label>
              <select
                id="unit-brand-filter"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                disabled={brandListStatus !== 'success'}
                className="w-full max-w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50 sm:w-44"
              >
                <option value="all">전체 브랜드</option>
                <option value="none">브랜드 없음</option>
                {brandList.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className="mb-1 block text-xs text-gray-500">정가 범위</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  aria-label="최저 정가"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  placeholder="최저가"
                  min={0}
                  className="w-24 min-w-0 rounded border border-gray-300 px-2 py-2 text-sm"
                />
                <span className="text-gray-400">~</span>
                <input
                  type="number"
                  aria-label="최고 정가"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="최고가"
                  min={0}
                  className="w-24 min-w-0 rounded border border-gray-300 px-2 py-2 text-sm"
                />
                <span className="text-xs text-gray-500">원</span>
              </div>
            </div>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={resetAllFilters}
                className="rounded px-2 py-2 text-sm text-gray-500 hover:text-gray-900"
              >
                필터 초기화
              </button>
            )}
          </div>
          {brandListStatus === 'error' && (
            <div
              role="alert"
              className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-5 py-3 text-xs text-gray-600"
            >
              <span>브랜드 목록을 불러오지 못했습니다.</span>
              <button
                type="button"
                onClick={() => invalidateQueriesByUrlPart(queryClient, 'gift-item-brand')}
                className="rounded px-2 py-1 text-indigo-700"
              >
                브랜드 다시 시도
              </button>
            </div>
          )}
          {status === 'success' && (
            <p
              role="status"
              className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500"
            >
              전체 {data.length}개 중 {filteredData.length}개 표시 · 브랜드별 정가 낮은 순
            </p>
          )}
          {status === 'loading' || status === 'idle' ? (
            <div role="status" className="center-box gap-3 py-16">
              <Loading />
              <span className="text-sm text-gray-500">선택유닛을 불러오는 중입니다.</span>
            </div>
          ) : status === 'error' ? (
            <div role="alert" className="border-t border-gray-200 px-5 py-14 text-center">
              <p className="mb-4 text-sm text-gray-600">
                선택유닛 목록을 불러오지 못했습니다.
              </p>
              <button
                type="button"
                onClick={() =>
                  invalidateQueriesByUrlPart(queryClient, 'gift-choice-unit')
                }
                className={secondaryButton}
              >
                다시 시도
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="border-t border-gray-200 px-5 py-14 text-center">
              <h2 className="text-base font-semibold">
                {hasActiveFilter
                  ? '검색 조건에 맞는 선택유닛이 없습니다.'
                  : '등록된 선택유닛이 없습니다.'}
              </h2>
              <p className="mb-5 mt-2 text-sm text-gray-500">
                {hasActiveFilter
                  ? '검색어나 브랜드, 가격 조건을 변경해 보세요.'
                  : '선택유닛을 등록한 후 쿠폰 상품에서 연결해 주세요.'}
              </p>
              {hasActiveFilter ? (
                <button
                  type="button"
                  onClick={resetAllFilters}
                  className={secondaryButton}
                >
                  검색 조건 초기화
                </button>
              ) : (
                <Link to={newUnitUrl} className={primaryButton}>
                  첫 선택유닛 등록
                </Link>
              )}
            </div>
          ) : (
            <table className="block w-full text-left xl:table">
              <thead className="hidden border-t border-gray-200 bg-gray-50 text-xs font-medium text-gray-500 xl:table-header-group">
                <tr>
                  {['선택유닛', '가격', '연결 원상품', '판매 상태', '관리'].map(
                    (title) => (
                      <th key={title} scope="col" className="px-5 py-3">
                        {title}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              {groupedData.map((group) => (
                <tbody key={group.brandId} className="block xl:table-row-group">
                  <tr className="block border-t border-gray-200 bg-gray-50 xl:table-row">
                    <th
                      colSpan={5}
                      scope="rowgroup"
                      className="block px-5 py-3 text-sm font-medium text-gray-700 xl:table-cell"
                    >
                      {group.brandName}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {group.items.length}개
                      </span>
                    </th>
                  </tr>
                  {group.items.map(renderItem)}
                </tbody>
              ))}
            </table>
          )}
        </section>
      </div>
    </div>
  );
};
